import moment from "moment";
import { Cell } from "react-table";
import { ProductModel } from "src/api/product/product-types";
import { BaseTooltip } from "src/components";

import DetailLink from "src/components/DetailLink";
import ProductStatusLabel from "src/components/ProductStatusLabel";
import ProductTypeChangeLabel from "src/components/ProductTypeChangeLabel";
import { numberToStringWithComma, ViewYmdFormat } from "src/utils";
import { PagePath } from "../details";
import { ProductStatusT, salePeriodTypeOptions } from "../product-types";
import IsUsedCell from "./IsUsedCell";

// 승인 유형 라벨
const approvalTypeLabel = (approvalType?: string) =>
  approvalType === "AUTO" ? "자동승인" : approvalType === "ADMIN" ? "관리자승인" : "-";

// 납부 유형 라벨
const costTypeLabel = (costType?: string) =>
  costType === "COST_BILLING_MONTHLY" ? "월납" : costType === "COST_BILLING_ONE_TIME" ? "일시납" : "-";

/*
  공간상품 테이블 columns
  순서: 상품 ID | 상품명 | 건물명 | 상품 유형 | 승인 유형 | 이용여부 | 계약 가능 여부 | 공개 유형 | 판매 방식 | 보증금(원) | 판매가(원) | 납부 유형 | 최종 수정일시
*/
const ProductColumns = [
  {
    Header: "상품 ID",
    accessor: "id",
    width: 120,
    Cell: (props: Cell<ProductModel>) => {
      const detailPath = `${PagePath.product.detail.replace(":id", `${props.row.original.id}`)}`;

      return (
        <div>
          <DetailLink to={detailPath} className="text-underline">
            <BaseTooltip contents={props?.value || ""} />
          </DetailLink>
        </div>
      );
    },
    sticky: "left",
  },
  {
    Header: "상품명",
    accessor: "productName",
    width: 240,
    // Interaction: 상품명 선택 시 상세 이동 미제공
    Cell: (props: Cell<ProductModel>) => {
      return (
        <div className="flex-center-start w-100">
          <BaseTooltip contents={props?.value || "-"} />
        </div>
      );
    },
  },
  {
    Header: "건물명",
    accessor: "buildingName",
    width: 120,
    Cell: (props: Cell<ProductModel>) => {
      return (
        <div className="flex-center-start w-100">
          <BaseTooltip contents={props?.value ?? "-"} />
        </div>
      );
    },
  },
  {
    Header: "상품 유형",
    accessor: "productType",
    width: 100,
    Cell: (props: Cell<ProductModel>) => (
      <div className="w-100 flex-center-center">
        <ProductTypeChangeLabel productType={props.row.original.productType} />
      </div>
    ),
  },
  {
    Header: "승인 유형",
    accessor: "approvalType",
    width: 100,
    Cell: ({ value }: Cell<ProductModel>) => <p className="w-100 text-center">{approvalTypeLabel(value)}</p>,
  },
  {
    Header: "이용여부",
    accessor: "isUsed",
    width: 80,
    Cell: (props: Cell<ProductModel>) => (
      <div className="w-100 text-center">
        <IsUsedCell {...props} />
      </div>
    ),
  },
  {
    Header: "계약 가능 여부",
    accessor: "isContractAvail",
    width: 120,
    Cell: (props: Cell<ProductModel>) => (
      <p className="w-100 text-center">{props.row?.original.isContractAvail ? "가능" : "불가능"}</p>
    ),
  },
  {
    Header: "공개 유형",
    accessor: "status",
    width: 100,
    Cell: (props: Cell<ProductModel>) => (
      <div className="w-100 text-center">
        {props.row.original.status ? <ProductStatusLabel status={props.row.original.status as ProductStatusT} /> : "-"}
      </div>
    ),
  },
  {
    Header: "판매 방식",
    accessor: "salePeriodType",
    width: 100,
    Cell: ({ value }: Cell<ProductModel>) => (
      <p className="w-100 text-center">{salePeriodTypeOptions.find((option) => option.value === value)?.label || "-"}</p>
    ),
  },
  {
    Header: "보증금(원)",
    accessor: "deposit",
    width: 130,
    // 데이터 타입 변경: 콤마 표기, 호버 제거, 줄바꿈 허용, 우측 정렬
    Cell: (props: Cell<ProductModel>) => {
      const deposit = props?.row?.original?.deposit;
      return <p className="w-100 text-right">{deposit ? numberToStringWithComma(Number(deposit)) : "-"}</p>;
    },
  },
  {
    Header: "판매가(원)",
    accessor: "rentalCost",
    width: 120,
    // 판매가에서 납부유형 분리, 콤마 표기, 호버 제거, 줄바꿈 허용, 우측 정렬
    Cell: (props: Cell<ProductModel>) => {
      const price = props?.row.original?.productionPrice;

      if (!price || price === -1) {
        return <p className="w-100 text-right">-</p>;
      }

      return <p className="w-100 text-right">{numberToStringWithComma(Number(price))}</p>;
    },
  },
  {
    Header: "납부 유형",
    accessor: "costType",
    width: 100,
    Cell: ({ value }: Cell<ProductModel>) => <p className="w-100 text-center">{costTypeLabel(value)}</p>,
  },
  {
    Header: "최종 수정일시",
    accessor: "modifiedDate",
    width: 160,
    // 최종 수정일 → 최종 수정일시(날짜+시간), 호버 제거, 줄바꿈 허용, 가운데 정렬
    Cell: ({ value }: Cell<ProductModel>) => (
      <p className="w-100 text-center">{value ? moment(value).format(ViewYmdFormat.FULL) : "-"}</p>
    ),
  },
];

export default ProductColumns;
